import Dialog from '../../1. Core/Controls/Dialog/Dialog';
import NP from '../../1. Core/NP';
import TextField from '../../1. Core/Controls/TextField/TextField';
import Button from '../../1. Core/Controls/Button/Button';
import DataTable from '../../1. Core/Controls/DataTable/DataTable';
import IconToggleButton from '../../1. Core/Controls/IconToggleButton/IconToggleButton';
import ImageTextField from '../../1. Core/Controls/TextField/ImageTextField';
import {ServerAPI} from '../../1. Core/ServerAPI';

export interface BroadcastProvider {
	id: number;
	name: string;
	logoUrl: string | null;
	sortOrder: number;
	active: boolean;
}

let providersCache: BroadcastProvider[] | null = null;

export async function loadBroadcastProviders(force = false): Promise<BroadcastProvider[]> {
	if (!force && providersCache?.length) {
		return providersCache;
	}
	providersCache = await ServerAPI.get<BroadcastProvider[]>('/broadcast-providers');
	return providersCache ?? [];
}

export function clearBroadcastProvidersCache() {
	providersCache = null;
}

class ProviderEditDialog extends Dialog {
	private _name: TextField;
	private _logo: ImageTextField;
	private _Provider: BroadcastProvider | null;

	_initFields(settings: object) {
		this._Provider = null;
		super._initFields(settings);
		this._Mode = 'FullHeight';
		this._Caption = 'Streaming platform';
		this._CloseOnFrameMouseDown = true;
		this._ShowHeader = true;
		this._ShowCloseButton = true;
		this._OkRK = 'Save';
		this._AutoDestroy = true;
	}

	_render() {
		super._render();

		this._name = new TextField({
			FloatingLabel: 'Platform name',
			ParentNode: this._contentNode,
			Value: this._Provider?.name ?? '',
		});

		this._logo = new ImageTextField({
			ParentNode: this._contentNode,
			// Same S3 presign as athlete/tournament logos (proven in admin).
			UploadApiPath: '/athlete/creates3Link',
			TextFieldSettings: {
				FloatingLabel: 'Logo URL',
				Value: this._Provider?.logoUrl ?? '',
			},
		});
	}

	_onOkButtonClick() {
		const name = this._name.getValue().trim();
		if (!name) {
			NP.showMessage({Content: 'Enter platform name'});
			return;
		}
		ServerAPI.post('/broadcast-providers', {
			id: this._Provider?.id ?? null,
			name,
			logoUrl: this._logo.getValue()?.trim() || null,
			sortOrder: this._Provider?.sortOrder ?? null,
			active: this._Provider?.active ?? true,
		}).then(() => {
			clearBroadcastProvidersCache();
			this.EvOkButtonClick.fire(this);
			this.hide();
		});
	}
}

export default class BroadcastProvidersDialog extends Dialog {
	private _dataTable: DataTable;

	_initFields(settings: object) {
		super._initFields(settings);
		this._Mode = 'FullHeight';
		this._Caption = 'Streaming platforms';
		this._CloseOnFrameMouseDown = true;
		this._ShowHeader = true;
		this._ShowCloseButton = true;
		this._ShowButtons = false;
		this._AutoDestroy = true;
		this._Width = 850;
		this._ContentAutoFit = true;
	}

	_render() {
		super._render();
		this.addClass('nopad');

		this.setContent(
			(this._dataTable = new DataTable({
				Class: 'app-list--avatars mdc-data-table--margin data-table-battle',
				MultiSelect: false,
				ShowRowIndex: false,
				ImageWidth: 48,
				Columns: [
					{Content: 'Name', PropertyContent: 'name', Width: '35%'},
					{Content: 'Logo', PropertyContent: 'logoUrl', Width: '45%'},
					{Content: 'Order', PropertyContent: 'sortOrder', Width: '10%'},
				],
				ShowPaginator: false,
				EvItemClick: (s: object, e: { Item: BroadcastProvider }) => {
					new ProviderEditDialog({
						Provider: e.Item,
						EvOkButtonClick: () => this.refresh(),
					}).show();
				},
			})),
		);

		this._topAppBar.addItem(
			new IconToggleButton({
				FontImage: NP.FontImages.add,
				Tooltip: NP.RM.getString('Create'),
				EvClick: () => {
					new ProviderEditDialog({
						EvOkButtonClick: () => this.refresh(),
					}).show();
				},
			}),
		);

		this.refresh();
	}

	async refresh() {
		const rows = await loadBroadcastProviders(true);
		this._dataTable.setRows(rows);
	}
}
